/**
 * V70 — 게임별 오픈 기간 설정 섹션 (관리자 UI 재사용 컴포넌트)
 */
import React, { useEffect, useState } from 'react';
import { Calendar } from 'lucide-react';
import {
  computeGamePeriod, datetimeLocalToTimestamp, timestampToDatetimeLocal,
  periodBadge, formatDiff, ALWAYS_OPEN_BADGE, type GamePeriodResult,
} from './gamePeriod';

type PeriodMode = 'always' | 'scheduled';

export interface OpenPeriodValue {
  openStartAt: number | null;
  openEndAt: number | null;
}

export interface OpenPeriodAdminSectionProps {
  gameLabel: string;              // "엔글 타자왕" | "엔글 마블"
  enabled: boolean;                // 게임 자체 ON/OFF (상위에서 전달)
  initialValue: OpenPeriodValue;
  mockBadge?: string;              // "Phase 2 mock — localStorage" 등
  onSave: (next: OpenPeriodValue) => Promise<void>;
}

export function OpenPeriodAdminSection(p: OpenPeriodAdminSectionProps) {
  const [mode, setMode] = useState<PeriodMode>(
    p.initialValue.openStartAt == null && p.initialValue.openEndAt == null ? 'always' : 'scheduled'
  );
  const [startStr, setStartStr] = useState(timestampToDatetimeLocal(p.initialValue.openStartAt));
  const [endStr, setEndStr] = useState(timestampToDatetimeLocal(p.initialValue.openEndAt));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    // initialValue 재로드 (저장 후 서버 응답 반영)
    const hasPeriod = p.initialValue.openStartAt != null || p.initialValue.openEndAt != null;
    setMode(hasPeriod ? 'scheduled' : 'always');
    setStartStr(timestampToDatetimeLocal(p.initialValue.openStartAt));
    setEndStr(timestampToDatetimeLocal(p.initialValue.openEndAt));
  }, [p.initialValue.openStartAt, p.initialValue.openEndAt]);

  // 모드에 따라 저장될 값 계산
  const parsed = mode === 'always'
    ? { openStartAt: null, openEndAt: null }
    : {
        openStartAt: datetimeLocalToTimestamp(startStr),
        openEndAt: datetimeLocalToTimestamp(endStr),
      };
  const period: GamePeriodResult = computeGamePeriod({
    enabled: p.enabled,
    openStartAt: parsed.openStartAt,
    openEndAt: parsed.openEndAt,
  });
  const badge = period.isAlwaysOpen ? ALWAYS_OPEN_BADGE : periodBadge(period.state);

  const handleSave = async () => {
    setErr(null); setMsg(null);
    if (mode === 'scheduled' && parsed.openStartAt == null && parsed.openEndAt == null) {
      setErr('기간 지정 모드에서는 시작 또는 종료 일시를 1개 이상 입력해주세요.');
      return;
    }
    if (parsed.openStartAt != null && parsed.openEndAt != null && parsed.openStartAt >= parsed.openEndAt) {
      setErr('종료 일시는 시작 일시보다 뒤여야 합니다.');
      return;
    }
    setSaving(true);
    try {
      await p.onSave(parsed);
      setMsg(mode === 'always' ? '상시 오픈으로 저장되었습니다.' : '기간이 저장되었습니다.');
    } catch (e: any) {
      setErr(e?.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 14, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, marginBottom: 12 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: '#0369a1', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Calendar size={14} /> {p.gameLabel} — 오픈 기간
        {p.mockBadge && <span style={{ fontSize: 10, color: '#92400e', background: '#fef3c7', padding: '1px 6px', borderRadius: 10, marginLeft: 4 }}>{p.mockBadge}</span>}
      </h3>
      <div style={{ fontSize: 12, color: '#0c4a6e', marginBottom: 10, lineHeight: 1.6 }}>
        시작/종료 일시 미설정 시 상시 오픈. KST 기준 — 브라우저 로컬 시간대로 입력.
      </div>

      {/* 모드 선택 radio (사용자 지시 §A) */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 12, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#0c4a6e', cursor: 'pointer' }}>
          <input
            type="radio"
            name={`period-mode-${p.gameLabel}`}
            checked={mode === 'always'}
            onChange={() => setMode('always')}
          />
          상시 오픈 <span style={{ color: '#64748b', fontSize: 11 }}>(기본값, 날짜 미설정)</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#0c4a6e', cursor: 'pointer' }}>
          <input
            type="radio"
            name={`period-mode-${p.gameLabel}`}
            checked={mode === 'scheduled'}
            onChange={() => setMode('scheduled')}
          />
          기간 지정
        </label>
      </div>

      <div
        style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10,
          marginBottom: 10,
          opacity: mode === 'scheduled' ? 1 : 0.45,
          pointerEvents: mode === 'scheduled' ? 'auto' : 'none',
          transition: 'opacity 160ms ease',
        }}
      >
        <label style={{ fontSize: 12 }}>
          <div style={{ color: '#334155', fontWeight: 500, marginBottom: 4 }}>시작 일시</div>
          <input
            type="datetime-local"
            value={startStr}
            onChange={(e) => setStartStr(e.target.value)}
            disabled={mode !== 'scheduled'}
            style={{ width: '100%', padding: 6, border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13 }}
          />
        </label>
        <label style={{ fontSize: 12 }}>
          <div style={{ color: '#334155', fontWeight: 500, marginBottom: 4 }}>종료 일시</div>
          <input
            type="datetime-local"
            value={endStr}
            onChange={(e) => setEndStr(e.target.value)}
            disabled={mode !== 'scheduled'}
            style={{ width: '100%', padding: 6, border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13 }}
          />
        </label>
      </div>

      <div style={{ fontSize: 12, color: '#0c4a6e', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>현재 상태:</span>
        <span style={{ padding: '2px 8px', borderRadius: 10, background: badge.bg, color: badge.fg, fontSize: 11, fontWeight: 700 }}>
          {badge.label}
        </span>
        {!period.isAlwaysOpen && period.diffMs != null && (
          <span style={{ color: '#475569' }}>({period.state === 'before' ? '시작까지' : period.state === 'during' ? '종료까지' : '종료 후'} {formatDiff(period.diffMs)})</span>
        )}
      </div>

      <ul style={{ fontSize: 11, color: '#475569', paddingLeft: 18, margin: '0 0 10px', lineHeight: 1.8 }}>
        <li>변경 즉시 적용 (서버 시간 기준) — Phase 3 BE 는 Firestore Timestamp 로 재검증</li>
        <li>기존 기록·점수·주사위·코인 보존 (리셋 트리거 없음)</li>
        <li>오픈 전/비공개 상태에서는 리더보드도 비공개</li>
        <li>게임 ON/OFF 가 OFF 면 기간과 무관하게 전부 차단 (system_admin 포함)</li>
      </ul>

      {err && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 8, borderRadius: 4, fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {msg && <div style={{ background: '#dcfce7', color: '#166534', padding: 8, borderRadius: 4, fontSize: 12, marginBottom: 8 }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, background: saving ? 'var(--color-gray-300)' : '#0369a1', color: 'white', border: 'none', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer' }}
        >
          {saving ? '저장 중…' : mode === 'always' ? '상시 오픈으로 저장' : '기간 저장'}
        </button>
      </div>
    </div>
  );
}
